import { useEffect, useState, type ReactNode } from "react";
import { Button } from "@/components/ui/button";
import type { TicketAttachment } from "@/types/ticket.types";
import { Image as ImageIcon, Loader2, Upload } from "lucide-react";

interface TicketAttachmentPreviewProps {
  ticketId: string;
  attachment: TicketAttachment;
  fetchBlob: (ticketId: string, attachmentId: string) => Promise<Blob>;
  onOpen: () => void;
  t: (key: string) => string;
  extraAction?: ReactNode;
}

const formatBytes = (value: number) => {
  if (value < 1024) return `${value} B`;
  if (value < 1024 * 1024) return `${(value / 1024).toFixed(1)} KB`;
  return `${(value / (1024 * 1024)).toFixed(2)} MB`;
};

export function TicketAttachmentPreview({
  ticketId,
  attachment,
  fetchBlob,
  onOpen,
  t,
  extraAction,
}: TicketAttachmentPreviewProps) {
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);
  const isImage = attachment.contentType.startsWith("image/");

  useEffect(() => {
    if (typeof window === "undefined") return;
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  const loadPreview = async () => {
    if (!isImage || isLoading) return;
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
      return;
    }
    setIsLoading(true);
    setHasError(false);
    try {
      const blob = await fetchBlob(ticketId, attachment.id);
      const objectUrl = URL.createObjectURL(blob);
      setPreviewUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return objectUrl;
      });
    } catch {
      setHasError(true);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-2 rounded-md border border-border p-2">
      <button
        type="button"
        className="flex h-28 w-full items-center justify-center overflow-hidden rounded-md border border-border bg-input-background"
        onClick={() => void loadPreview()}
        title={attachment.filename}
      >
        {previewUrl ? (
          <img
            src={previewUrl}
            alt={attachment.filename}
            loading="lazy"
            className="h-full w-full object-cover"
          />
        ) : (
          <span className="text-secondary" aria-label={attachment.filename}>
            {isLoading ? (
              <Loader2 className="h-5 w-5 animate-spin" />
            ) : (
              <ImageIcon className="h-5 w-5" />
            )}
          </span>
        )}
      </button>
      {hasError && <p className="text-xs text-secondary">{t("common.genericError")}</p>}

      <div className="flex min-w-0 flex-wrap items-center gap-2 text-xs">
        <button
          type="button"
          className="min-w-0 flex-1 break-all text-left underline-offset-2 hover:underline [overflow-wrap:anywhere]"
          onClick={() => void loadPreview()}
          title={attachment.filename}
        >
          {attachment.filename}
        </button>
        <span className="text-secondary">{formatBytes(attachment.sizeBytes)}</span>
      </div>

      <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
        <Button
          size="sm"
          variant="outline"
          className="h-7 px-2 w-full sm:w-auto"
          onClick={onOpen}
        >
          <Upload className="mr-1 h-3 w-3" />
          {t("account.ticketOpenAttachment")}
        </Button>
        {extraAction}
      </div>
    </div>
  );
}
